import React, { useEffect, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setAddItemToCart,
  setIncreaseItemQTY,
  setDecreaseItemQTY,
  setRemoveItemFromCart,
} from "../../../app/CartSlice";
import {
  PlusOutlined,
  MinusOutlined,
  ShoppingCartOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const CardComponent = ({ product, productInfo, currCart, handleCartPopUpChange, showThreeCards }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedSize, setSelectedSize] = useState(null);
  const [currIdx, setCurrIdx] = useState(0);
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);
  const [selectedProductMrp, setSelectedProductMrp] = useState(0);
  const [selectedDiscount, setSelectedDiscount] = useState(0);
  const [currProduct, setCurrProduct] = useState([]);

  // Subscribe to Redux cart items
  const cartItems = useSelector((state) => state.cart?.cartItems) || [];

  useEffect(() => {
    getProductDetailInfo();
  }, [product]);

  const getProductDetailInfo = async () => {
    if (productInfo) {
      const response = await productInfo();
      setCurrProduct(response);
    }
  };

  useEffect(() => {
    if (product && product.productPackageDetails && product.productPackageDetails.length > 0) {
      const defaultSize = product.productPackageDetails[0];
      handleSize(defaultSize, 0);

      const selectedProduct = currProduct?.find(
        (item) => item.packageCode === defaultSize.packageSize
      );

      if (selectedProduct) {
        setSelectedProductMrp(selectedProduct.unitPrice);
        setSelectedProductPrice(selectedProduct.finalPrice);
        setSelectedDiscount(selectedProduct.discountPercentage || 0);
      }
    }
  }, [product, currProduct]);

  const handleSize = (size, idx) => {
    setSelectedSize(size);
    setCurrIdx(idx);
    const sizeName = typeof size === "object" ? size.packageSize : size;
    const selected = currProduct?.find((item) => item.packageCode === sizeName);
    if (selected) {
      setSelectedProductMrp(selected.unitPrice);
      setSelectedProductPrice(selected.finalPrice);
      setSelectedDiscount(selected.discountPercentage || 0);
    }
  };

  // Extract current size key
  const currentSizeKey =
    typeof selectedSize === "object" && selectedSize?.packageSize
      ? String(selectedSize.packageSize).trim().toLowerCase()
      : String(selectedSize || "standard").trim().toLowerCase();

  // Find if this product with the currently selected size is in the cart
  const cartItem = cartItems.find((item) => {
    const itemSizeKey =
      typeof item.selectedSize === "object" && item.selectedSize?.packageSize
        ? String(item.selectedSize.packageSize).trim().toLowerCase()
        : String(item.selectedSize || "standard").trim().toLowerCase();
    return item.id === product?.id && itemSizeKey === currentSizeKey;
  });

  const cartQuantity = cartItem ? Number(cartItem.cartQuantity) || 0 : 0;

  const handleAddToCart = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    let priceToUse = selectedProductPrice;
    if (!priceToUse && currProduct && currProduct.length > 0) {
      const sizeName = typeof selectedSize === "object" ? selectedSize?.packageSize : selectedSize;
      const selected = currProduct.find((item) => item.packageCode === sizeName);
      if (selected) {
        priceToUse = selected.finalPrice || selected.unitPrice;
      } else if (currProduct[0]) {
        priceToUse = currProduct[0].finalPrice || currProduct[0].unitPrice;
      }
    }
    const numericPrice = Number(priceToUse || selectedProductMrp || 0);
    const sizeToUse =
      selectedSize ||
      (product?.productPackageDetails && product.productPackageDetails[0]) || {
        packageSize: "Standard",
      };

    const itemTobeAdded = {
      ...product,
      selectedProductPrice: numericPrice,
      price: numericPrice,
      selectedSize: sizeToUse,
    };

    dispatch(setAddItemToCart(itemTobeAdded));
  };

  const handleIncrement = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (cartItem) {
      dispatch(setIncreaseItemQTY(cartItem.cartItemId || cartItem.id));
    }
  };

  const handleDecrement = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (cartItem) {
      if (cartQuantity > 1) {
        dispatch(setDecreaseItemQTY(cartItem.cartItemId || cartItem.id));
      } else {
        dispatch(setRemoveItemFromCart(cartItem));
      }
    }
  };

  const handleClick = () => {
    navigate(`/productdetail/${product.id}`);
    window.scrollTo(0, 0);
  };

  const imageSrc = product?.imageName
    ? product.imageName.startsWith("http") || product.imageName.startsWith("data:")
      ? product.imageName
      : `${process.env.REACT_APP_IMAGE_URL || ""}${product.imageName}`
    : product?.imageUrl || "";

  return (
    <div className="w-full max-w-[320px] mx-auto bg-white rounded-[32px] border border-gray-100 shadow-[0_10px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_15px_40px_rgba(161,122,23,0.12)] transition-all duration-300 relative flex flex-col justify-between overflow-hidden group">
      {/* Discount Badge */}
      {selectedDiscount > 0 && (
        <div className="absolute top-4 left-4 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold text-xs py-1 px-3.5 rounded-full shadow-md z-10 tracking-wide">
          {selectedDiscount}% OFF
        </div>
      )}

      {/* 3/4 Height Image Container */}
      <div
        className="w-full h-80 flex items-center justify-center cursor-pointer overflow-hidden relative bg-[#FAF8F5]"
        onClick={handleClick}
      >
        <img
          alt={product?.name || "Product Image"}
          src={imageSrc}
          className="w-full h-full object-cover zoom-image"
        />
      </div>

      {/* Product Details Section below the image */}
      <div className="p-2 flex-1 flex flex-col justify-between">
        <div>
          {/* Product Title */}
          <h3
            className="font-sans font-bold text-gray-800 text-lg hover:text-[#A17A17] transition-colors cursor-pointer min-h-[3rem] leading-snug line-clamp-2"
            onClick={handleClick}
            title={product?.name}
          >
            {product?.name}
          </h3>

          {/* Product Description */}
          {(product?.titleDescription || product?.description) && (
            <p
              className="text-sm text-gray-400 font-normal line-clamp-1 mt-1 mb-2 leading-relaxed cursor-help hover:text-gray-600 transition-colors"
              title={product.titleDescription || product.description}
            >
              {product.titleDescription || product.description}
            </p>
          )}

          {/* Size Pills & Pricing Row */}
          <div className="flex justify-between items-center gap-2 mb-2">
            <div className="flex flex-wrap gap-1">
              {product?.productPackageDetails?.map((size, idx) => {
                const isSelected =
                  (selectedSize === size || (selectedSize?.packageSize && selectedSize.packageSize === size.packageSize)) &&
                  currIdx === idx;
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`px-2 py-1 text-[10px] rounded-full font-semibold transition-all duration-150 cursor-pointer ${
                      isSelected
                        ? "bg-[#A17A17] text-white shadow-sm border border-[#A17A17]"
                        : "bg-[#FFF0C0]/40 text-[#A17A17] border border-amber-200/50 hover:bg-[#FFF0C0]"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSize(size, idx);
                    }}
                  >
                    {size.packageSize}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-col items-end">
              {selectedProductMrp > selectedProductPrice && (
                <span className="text-[10px] text-gray-400 line-through">
                  INR {selectedProductMrp}
                </span>
              )}
              <span className="text-lg font-bold text-[#A17A17] whitespace-nowrap">
                INR {selectedProductPrice || selectedProductMrp || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Add to Cart / Quantity Counter Controller */}
        <div className="w-full">
          {cartQuantity === 0 ? (
            <button
              type="button"
              onClick={handleAddToCart}
              className="w-full h-12 bg-[#A17A17] hover:bg-[#866100] text-white font-bold text-sm rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2 active:scale-98 cursor-pointer"
            >
              <ShoppingCartOutlined className="text-base" />
              <span>Add To Cart</span>
            </button>
          ) : (
            <div className="w-full h-12 bg-[#FFF8E7] border-2 border-[#A17A17] rounded-2xl flex items-center justify-between px-3 shadow-inner">
              <button
                type="button"
                onClick={handleDecrement}
                className="w-8 h-8 rounded-lg bg-[#A17A17] hover:bg-[#866100] text-white flex items-center justify-center transition-all duration-150 active:scale-90 shadow-sm cursor-pointer"
                title={cartQuantity === 1 ? "Remove from cart" : "Decrease quantity"}
              >
                {cartQuantity === 1 ? (
                  <DeleteOutlined className="text-xs" />
                ) : (
                  <MinusOutlined className="text-xs" />
                )}
              </button>

              <div className="flex flex-col items-center justify-center select-none">
                <span className="text-base font-bold text-[#4C3000] leading-none">
                  {cartQuantity}
                </span>
                <span className="text-[9px] text-[#A17A17] font-bold uppercase tracking-wider leading-none mt-0.5">
                  in cart
                </span>
              </div>

              <button
                type="button"
                onClick={handleIncrement}
                className="w-8 h-8 rounded-lg bg-[#A17A17] hover:bg-[#866100] text-white flex items-center justify-center transition-all duration-150 active:scale-90 shadow-sm cursor-pointer"
                title="Increase quantity"
              >
                <PlusOutlined className="text-xs" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardComponent;
